// Where Karim's life-context docs live in the docs layer, in one place.
//
// The docs layer is split into app knowledge (mission-control/) and life
// context (life/background/). Health, food and workout are life context:
//
//     mission-control/health/*.md   -> life/background/health/*.md
//     mission-control/food/*.md     -> life/background/food/*.md
//     mission-control/workout/*.md  -> life/background/workout/*.md
//
// File names stay the same. The move finished on 11.10.2026, so every area
// now reads the new path only, and a missing doc is a 404 rather than a quiet
// read of the old copy:
//
//   · food and workout: fallbackToOld off since 23.10.2026. The switch and
//     oldDocPath stay, so a later move can turn it back on for one area.
//   · health: new path only. Since 10.10 the old health files hold a one-line
//     pointer, not the content, so falling back to them would show pointer
//     text as if it were the document.
//
// Nothing else in this package spells these paths out. Add a doc area here.

export const LIFE_ROOT = "life/background";
export const APP_ROOT = "mission-control";

export type LifeArea = "health" | "food" | "workout";

const AREAS: Record<LifeArea, { fallbackToOld: boolean }> = {
  health: { fallbackToOld: false },
  food: { fallbackToOld: false },
  workout: { fallbackToOld: false },
};

export const lifeDocPath = (area: LifeArea, file: string) => `${LIFE_ROOT}/${area}/${file}`;
export const oldDocPath = (area: LifeArea, file: string) => `${APP_ROOT}/${area}/${file}`;

export type ReadDoc = (path: string) => Promise<{ content: string }>;

/**
 * Read one life-context doc: the new path first, then (food and workout only)
 * the old one when the new one does not exist. Only a 404 falls back; any
 * other failure is a real failure and is thrown as it came.
 */
export async function readLifeDoc(read: ReadDoc, area: LifeArea, file: string): Promise<{ content: string; path: string }> {
  const fresh = lifeDocPath(area, file);
  try {
    return { ...(await read(fresh)), path: fresh };
  } catch (e) {
    if ((e as { status?: number })?.status !== 404 || !AREAS[area].fallbackToOld) throw e;
    const old = oldDocPath(area, file);
    return { ...(await read(old)), path: old };
  }
}
